import { useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Link2,
  Minus,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Wallet,
} from 'lucide-react';

import type { TransactionMatchedBankLine } from '@/types/bank-reconciliation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { shortOfToTheCent } from '@/utils/number-operations';

export interface BankConfirmationEntry {
  id: string;
  value_date: string;
  amount: number;
  currency: string;
  counterparty_iban: string;
  payment_reference: string | null;
  source: 'MANUAL' | 'PSD2';
  /** Set when this entry mirrors a drawdown payment, which is where that money
   *  is listed. Absent on the captable side, where no drawdown can source one. */
  source_drawdown_payment_id?: string | null;
  /** Set on a cap table transaction, whose entries sit on each fund's share. */
  fund_name?: string;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
}

interface BankConfirmationSectionProps {
  required: boolean;
  entries: BankConfirmationEntry[];
  canEdit: boolean;
  isLoading?: boolean;
  isError?: boolean;
  isDeleting?: boolean;
  expectedAmount?: number | null;
  expectedCurrency?: string | null;
  verified?: boolean;
  matchedLines?: TransactionMatchedBankLine[];
  onMatchBankLine?: () => void;
  /** Omitted on a capital call, whose payments belong to the notices they
   *  settle. */
  onAdd?: () => void;
  onEdit: (entryId: string) => void;
  onDelete: (entryId: string) => void;
  /** Replaces the flat matched list rather than joining it, so pass it only
   *  with something to show. Coverage still comes from matchedLines. */
  children?: ReactNode;
}

function emptyEntriesMessage({
  canEdit,
  canMatch,
}: {
  canEdit: boolean;
  canMatch: boolean;
}): string {
  if (!canEdit) return 'No bank entries yet.';
  if (canMatch) {
    return 'Nothing confirms this transaction yet. Match a bank transaction from the feed, or record an entry by hand.';
  }
  return 'No bank entries yet. Add one to confirm the cash movement.';
}

function BankEntriesPlaceholder({
  isLoading,
  isHidden,
  canEdit,
  canMatch,
}: {
  isLoading?: boolean;
  isHidden: boolean;
  canEdit: boolean;
  canMatch: boolean;
}) {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground py-3">Loading…</p>;
  }
  if (isHidden) return null;
  return (
    <div className="mt-3 flex items-start gap-2">
      <Clock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">
        {emptyEntriesMessage({ canEdit, canMatch })}
      </span>
    </div>
  );
}

export function BankConfirmationSection({
  required,
  entries,
  canEdit,
  isLoading,
  isError,
  isDeleting,
  expectedAmount,
  expectedCurrency,
  verified,
  matchedLines = [],
  onMatchBankLine,
  onAdd,
  onEdit,
  onDelete,
  children,
}: BankConfirmationSectionProps) {
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const pendingRemove = entries.find((entry) => entry.id === pendingRemoveId);

  if (!required) {
    return (
      <section className="px-6 py-5">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Bank confirmation</h3>
          <Badge variant="outline" className="font-normal">
            <Minus className="h-3 w-3 mr-1" />
            Not required
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          No cash movement, so no bank entry is expected.
        </p>
        {children}
      </section>
    );
  }

  const hasEntries = entries.length > 0;
  const showFund =
    new Set(entries.map((entry) => entry.fund_name).filter(Boolean)).size > 1;
  const hasMatchedLines = matchedLines.length > 0;
  const hasEvidence = hasEntries || hasMatchedLines;
  const summary = buildSummary(
    entries,
    matchedLines,
    expectedAmount,
    expectedCurrency
  );

  const handleConfirmRemove = () => {
    if (!pendingRemoveId) return;
    onDelete(pendingRemoveId);
    setPendingRemoveId(null);
  };

  return (
    <section className="px-6 py-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Bank confirmation</h3>
          <BankStatusBadge verified={verified ?? hasEvidence} />
        </div>
        {canEdit && (onMatchBankLine || onAdd) && (
          <div className="flex items-center gap-2">
            {onMatchBankLine && (
              <Button
                variant="outline"
                size="sm"
                onClick={onMatchBankLine}
                data-testid="match-bank-line-button"
              >
                <Link2 className="h-4 w-4 mr-2" />
                Match bank transaction
              </Button>
            )}
            {onAdd && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAdd}
                data-testid="add-bank-entry-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Bank entry
              </Button>
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Evidence of the cash movement behind this transaction - a bank line
        matched from the provider feed, or an entry recorded by hand. Multiple
        are allowed, e.g. split payments or netted fees.
      </p>

      {summary && hasEvidence && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>
            Only{' '}
            <span className="font-medium">
              {formatCurrency(summary.sum, { currency: summary.currency })}
            </span>{' '}
            of{' '}
            <span className="font-medium">
              {formatCurrency(summary.expected, { currency: summary.currency })}
            </span>{' '}
            confirmed
            {summary.note ? ` - ${summary.note}` : ''}.
          </span>
        </div>
      )}

      {isError && (
        <div className="mt-3 flex items-start gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Failed to load bank entries. Retry shortly or contact support if the
            error persists.
          </span>
        </div>
      )}

      {!isLoading && !isError && hasEntries ? (
        <Table className="mt-3" data-testid="bank-entries-list">
          <TableHeader>
            <TableRow>
              <TableHead>Value date</TableHead>
              {showFund && <TableHead>Fund</TableHead>}
              <TableHead>Amount</TableHead>
              <TableHead>Counterparty</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Added by</TableHead>
              <TableHead>Added at</TableHead>
              {canEdit && <TableHead className="w-[96px]" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{formatDate(entry.value_date)}</TableCell>
                {showFund && <TableCell>{entry.fund_name}</TableCell>}
                <TableCell className="font-medium">
                  {formatCurrency(entry.amount, { currency: entry.currency })}
                </TableCell>
                <TableCell className="max-w-[180px] truncate">
                  {entry.counterparty_iban}
                </TableCell>
                <TableCell className="max-w-[160px] truncate">
                  {entry.payment_reference || '-'}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-normal">
                    {entry.source}
                  </Badge>
                </TableCell>
                <TableCell>
                  {entry.created_by_name ??
                    (entry.created_by === null ? 'Automatic match' : '-')}
                </TableCell>
                <TableCell>{formatDate(entry.created_at)}</TableCell>
                {canEdit && (
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {entry.source === 'MANUAL' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(entry.id)}
                          aria-label="Edit bank entry"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingRemoveId(entry.id)}
                        disabled={isDeleting}
                        aria-label="Remove bank entry"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <BankEntriesPlaceholder
          isLoading={isLoading}
          isHidden={isError || hasMatchedLines}
          canEdit={canEdit}
          canMatch={Boolean(onMatchBankLine)}
        />
      )}

      {hasMatchedLines && !children && (
        <MatchedBankLines lines={matchedLines} />
      )}

      {children}

      <AlertDialog
        open={pendingRemoveId !== null}
        onOpenChange={(open) => !open && setPendingRemoveId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove bank entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRemove
                ? `Remove the ${formatCurrency(pendingRemove.amount, { currency: pendingRemove.currency })} entry dated ${formatDate(pendingRemove.value_date)}? Bank confirmation will be revoked and the transaction may revert below the required verification threshold.`
                : 'Remove this bank entry?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

// Bank lines the provider delivered and an operator matched to this
// transaction. They are settled ledger rows owned by reconciliation, so they
// are evidence to read here, never rows to edit.
function MatchedBankLines({ lines }: { lines: TransactionMatchedBankLine[] }) {
  // A line that settled an LP payment is listed against that investor, where
  // it says something this table cannot: whose money it was.
  const attributed = lines.filter((line) => line.settles_investor_payment);
  const unattributed = lines.filter((line) => !line.settles_investor_payment);

  if (unattributed.length === 0) {
    return attributed.length > 0 ? (
      <p className="mt-4 text-xs text-muted-foreground">
        {attributed.length} matched bank{' '}
        {attributed.length === 1 ? 'transaction is' : 'transactions are'} listed
        with the investors above.
      </p>
    ) : null;
  }

  return (
    <div className="mt-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        Matched bank transactions
      </p>
      {attributed.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          {attributed.length} more {attributed.length === 1 ? 'is' : 'are'}{' '}
          listed with the investors above.
        </p>
      )}
      <Table className="mt-2" data-testid="matched-bank-lines">
        <TableHeader>
          <TableRow>
            <TableHead>Value date</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Counterparty</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Account</TableHead>
            <TableHead>Matched by</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {unattributed.map((line) => (
            <TableRow key={line.id}>
              <TableCell>
                {formatDate(line.value_date ?? line.execution_date)}
              </TableCell>
              <TableCell className="font-medium">
                {formatCurrency(line.amount, {
                  currency: line.currency ?? undefined,
                })}
              </TableCell>
              <TableCell className="max-w-[180px] truncate">
                {line.counterparty_name || line.counterparty_reference || '-'}
              </TableCell>
              <TableCell className="max-w-[220px] truncate">
                {line.remittance_information || '-'}
              </TableCell>
              <TableCell className="max-w-[160px] truncate">
                {line.account_name}
              </TableCell>
              <TableCell>
                {line.is_auto ? (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Sparkles className="h-3 w-3" />
                    automatic
                  </span>
                ) : (
                  (line.matched_by_name ?? '-')
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface BankSummary {
  sum: number;
  expected: number;
  currency: string;
  note: string | null;
}

// Both kinds of evidence count towards the expected amount: a Ponto line that
// covers half and a manual entry for the rest leave nothing outstanding.
function buildSummary(
  entries: BankConfirmationEntry[],
  matchedLines: TransactionMatchedBankLine[],
  expectedAmount: number | null | undefined,
  expectedCurrency: string | null | undefined
): BankSummary | null {
  if (expectedAmount == null || !expectedCurrency) return null;
  if (expectedAmount <= 0) return null;

  const sum =
    entries
      .filter((e) => e.currency === expectedCurrency)
      .reduce((acc, e) => acc + e.amount, 0) +
    matchedLines
      .filter((line) => line.currency === expectedCurrency)
      .reduce((acc, line) => acc + Math.abs(Number(line.amount)), 0);
  if (!shortOfToTheCent(sum, expectedAmount)) return null;

  const hasMismatchedCurrency =
    entries.some((e) => e.currency !== expectedCurrency) ||
    matchedLines.some((line) => line.currency !== expectedCurrency);
  return {
    sum,
    expected: expectedAmount,
    currency: expectedCurrency,
    note: hasMismatchedCurrency
      ? 'entries in other currencies not summed'
      : null,
  };
}

export function BankStatusBadge({ verified }: { verified: boolean }) {
  if (verified) {
    return (
      <Badge
        variant="secondary"
        className="bg-emerald-50 text-emerald-700 border-emerald-200 font-normal dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900"
      >
        <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-600 dark:text-emerald-400" />
        Verified
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="border-amber-200 bg-amber-50 text-amber-800 font-normal dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300"
    >
      <Clock className="h-3 w-3 mr-1" />
      Pending
    </Badge>
  );
}
